// What the Remove Duplicate fix shows and what it changes. (Shared, Unknown and Not Billable rows are review-dialog/review-plan.ts.)
// Built from the agent MLS IDs (shared/agent-mls-ids); index.tsx beside it renders these, and the writes are the
// shared dialogs' fix steps.
import { ClientBoardResource } from "api/resource/client-board";
import { AgentMlsIds } from "model/agent-mls-ids";
import { alternateIdOf, isNone } from "shared/agent-mls-ids";

const boardName = (agentMlsIds: AgentMlsIds, boardId: number) => agentMlsIds.boards.find((b) => b.boardId === boardId)?.name ?? "";
const agentName = (agentMlsIds: AgentMlsIds, agentId: number) => agentMlsIds.agents.get(agentId)?.name ?? "";

// ---------- Keep one duplicate (Remove Duplicate) ----------
export interface KeepOneRow {
	clientBoardId: number;
	agentName: string;
	mlsId: string;
	alternateId: string | null;
	// The Added column, when the Office MLS IDs don't tell the rows apart.
	added: "Most recently" | "Earlier" | null;
}

export interface KeepOnePlan {
	boardId: number;
	mlsId: string;
	// The agent whose MLS ID it is, for Remove All.
	agentId: number;
	// The entry Remove All clears.
	entryId: number;
	boardName: string;
	prompt: string;
	// Remove All's confirmation.
	removeAll: { title: string; text: string };
	// Show the Added column.
	tied: boolean;
	rows: KeepOneRow[];
	// The row selected when the modal opens: the one with a Backup Office MLS ID, else the newest.
	suggested: number | null;
}

// Office MLS IDs compared to tell the rows apart: trimmed and case-insensitive, like the server's own rule. These are
// the rows' second column, not the Agent MLS IDs the service matches.
const sameAlternateId = (a: string | null, b: string | null) => (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();

// The slot the keep-one fix suggests: the one with a Backup Office MLS ID; if that doesn't decide it, the newest (highest
// id). For a Featured row whose value has one holder and more slots.
const dupKeeper = (agentMlsIds: AgentMlsIds, clientBoardId: number): number | null => {
	const holder = agentMlsIds.entries.find((entry) => entry.needsSlot && entry.clientBoardIds.includes(clientBoardId));
	const rows = holder ? holder.clientBoardIds.map((id) => agentMlsIds.rows.get(id)).filter((row): row is ClientBoardResource => !!row) : [];
	if (rows.length === 0) {
		return null;
	}
	const withAlternateId = rows.filter((row) => alternateIdOf(row) !== null);
	return (withAlternateId.length ? withAlternateId : rows).reduce((a, b) => (b.id > a.id ? b : a)).id;
};

export const keepOnePlan = (agentMlsIds: AgentMlsIds, entryId: number): KeepOnePlan | null => {
	const entry = agentMlsIds.entries.find((e) => e.id === entryId);
	if (!entry || entry.clientBoardIds.length < 2) {
		return null;
	}
	const rows = entry.clientBoardIds.map((id) => agentMlsIds.rows.get(id)).filter((r): r is NonNullable<typeof r> => !!r).sort((a, b) => a.id - b.id);
	const alternateIds = rows.map((r) => (isNone(r.officeId) ? null : r.officeId));
	const tied = rows.some((r, i) => rows.some((o, j) => j !== i && sameAlternateId(alternateIds[i], alternateIds[j])));
	const newest = rows.reduce((a, b) => (b.id > a.id ? b : a)).id;
	return {
		boardId: entry.boardId,
		mlsId: entry.mlsId,
		agentId: entry.agentId,
		entryId: entry.id,
		boardName: boardName(agentMlsIds, entry.boardId),
		removeAll: {
			title: `Remove ${entry.mlsId}?`,
			text: `${entry.mlsId} will no longer be associated with ${agentName(agentMlsIds, entry.agentId).split(" ")[0]}. Their ${boardName(agentMlsIds, entry.boardId)} listings `
				+ "won't show on Featured Listings or their bio page, and leads won't route to them from it.",
		},
		prompt: `${entry.mlsId} is on Featured Listings more than once for ${agentName(agentMlsIds, entry.agentId)} on ${boardName(agentMlsIds, entry.boardId)}. `
			+ "Choose the one to keep. The others will be removed.",
		tied,
		rows: rows.map((r, i) => ({
			clientBoardId: r.id,
			agentName: agentName(agentMlsIds, entry.agentId),
			mlsId: r.agentId,
			alternateId: alternateIds[i],
			added: tied ? (r.id === newest ? "Most recently" : "Earlier") : null,
		})),
		suggested: dupKeeper(agentMlsIds, rows[0].id),
	};
};
